import React, { useCallback, useMemo, useRef, useState } from 'react'
import type { ExploreCategory } from 'tripPlanner/pages/trip/Explore/common'
import { exploreCategories } from 'tripPlanner/pages/trip/Explore/common'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import CategoryDropdownItem from '../components/Trip/CategoryDropdownItem'
import type { TripData } from './useHomePageTripData'
import { useHomePageTripData } from './useHomePageTripData'

interface UseCategoryDropdownReturn {
  selectedCategory: ExploreCategory
  isDropdownOpen: boolean
  dropdownRef: React.RefObject<HTMLDivElement | null>
  dropdownItems: Array<React.ReactElement>
  handleDropdownToggle: () => void
  handleDropdownClose: () => void
  handleCategorySelect: (category: ExploreCategory) => void
}

interface UseOrderDropdownReturn {
  isOrderDropdownOpen: boolean
  orderDropdownRef: React.RefObject<HTMLDivElement | null>
  orderButtonRef: React.RefObject<HTMLButtonElement | null>
  orderDestinationItems: Array<React.ReactElement>
  selectedOrderTripData: TripData | null
  handleOrderDropdownToggle: () => void
  handleOrderDropdownClose: () => void
  handleOrderClick: (event: React.MouseEvent) => void
}

export function useCategoryDropdown(onCategoryChange?: (category: ExploreCategory) => void): UseCategoryDropdownReturn {
  const [selectedCategory, setSelectedCategory] = useState<ExploreCategory>(exploreCategories[0])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleDropdownToggle = useCallback(() => {
    setIsDropdownOpen(prev => !prev)
  }, [])

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleCategorySelect = useCallback((category: ExploreCategory) => {
    setSelectedCategory(category)
    setIsDropdownOpen(false)
    onCategoryChange?.(category)
  }, [onCategoryChange])

  const dropdownItems = useMemo(() =>
    exploreCategories.map((category) => (
      <CategoryDropdownItem
        key={category.label}
        category={category}
        isSelected={selectedCategory === category}
        onSelect={handleCategorySelect}
      />
    )), [selectedCategory, handleCategorySelect],
  )

  return {
    selectedCategory,
    isDropdownOpen,
    dropdownRef,
    dropdownItems,
    handleDropdownToggle,
    handleDropdownClose,
    handleCategorySelect,
  }
}

export function useOrderDropdown(orders: Array<App.Order>): UseOrderDropdownReturn {
  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<App.Order | undefined>(undefined)
  const orderDropdownRef = useRef<HTMLDivElement>(null)
  const orderButtonRef = useRef<HTMLButtonElement>(null)

  const selectedOrderTripData = useHomePageTripData({
    orders: selectedOrder ? [selectedOrder] : [],
  })

  const handleOrderDropdownToggle = useCallback(() => {
    setIsOrderDropdownOpen(prev => !prev)
  }, [])

  const handleOrderDropdownClose = useCallback(() => {
    setIsOrderDropdownOpen(false)
  }, [])

  const handleOrderClick = useCallback((event: React.MouseEvent) => {
    const orderId = event.currentTarget.getAttribute('data-order-id')
    const order = orders.find(order => order.id === orderId)
    if (order) {
      setSelectedOrder(order)
      setIsOrderDropdownOpen(false)
    }
  }, [orders])

  const orderDestinationItems = useMemo(() =>
    orders.map((order, index) => {
      const locationName = order.items.length > 0
        ? order.items[0].offer?.property?.locationHeading || ''
        : order.bedbankItems[0]?.offer?.property?.address?.city || ''

      return (
        <ClickableListItem
          key={`${order.id}-${index}`}
          title={locationName}
          size="medium"
          data-order-id={order.id}
          onClick={handleOrderClick}
        />
      )
    }), [orders, handleOrderClick])

  return {
    isOrderDropdownOpen,
    orderDropdownRef,
    orderButtonRef,
    orderDestinationItems,
    selectedOrderTripData,
    handleOrderDropdownToggle,
    handleOrderDropdownClose,
    handleOrderClick,
  }
}
